/**
 * Format a user object for display in the Users table
 * Preserves original bank and agencyDepartment objects while creating display versions
 * @param user - The user object from the API
 * @returns Formatted user object with bankDisplay field
 */
export function formatUserForDisplay(user: any) {
  // Store original bank and agencyDepartment objects
  const originalBank = user.bank;
  const originalAgencyDepartment = user.agencyDepartment;
  
  // Create display version for table
  let displayBankName: string;
  
  if (user.bank) {
    displayBankName = user.bank.name;
  } else if (user.roles?.some((role: any) => role.name.includes("monitoring_review"))) {
    displayBankName = "მონიტორინგის გადამხედველი";
  } else if (user.roles?.some((role: any) => role.name.includes("monitoring_cgs"))) {
    displayBankName = "მონიტორინგი (მიწის)";
  } else if (user.roles?.some((role: any) => role.name.includes("monitoring"))) {
    displayBankName = "მონიტორინგი";
  } else {
    displayBankName = "სააგენტო";
  }
  
  return {
    ...user,
    bankDisplay: displayBankName,
    bank: originalBank, // Keep original bank object
    agencyDepartment: originalAgencyDepartment, // Keep original agencyDepartment object
  };
}

/**
 * Determine the appropriate tab for a user based on their roles
 * @param roles - Array of role objects with name property
 * @param hasBank - Whether the user has a bank assigned
 * @returns The tab name (bank, agency, monitoring, monitoring_cgs, monitoring_review)
 */
export function getUserTabFromRoles(roles: any[], hasBank: boolean): string {
  if (roles?.some((role) => role.name === "monitoring")) return "monitoring";
  if (roles?.some((role) => role.name === "monitoring_cgs")) return "monitoring_cgs";
  if (roles?.some((role) => role.name === "monitoring_review")) return "monitoring_review";
  if (roles?.some((role) => role.name === "agency")) return "agency";
  if (hasBank) return "bank";
  return "bank";
}

/**
 * Check if a tab should be disabled based on user role flags and edit mode
 * @param tabName - The name of the tab to check
 * @param currentTab - The currently selected tab
 * @param isEditMode - Whether the dialog is in edit mode
 * @param roleFlags - Object containing role flags (isBank, isMonitoring, etc.)
 * @returns Whether the tab should be disabled
 */
export function isTabDisabled(
  tabName: string,
  currentTab: string,
  isEditMode: boolean,
  roleFlags: {
    isBank?: boolean;
    isMonitoring?: boolean;
    isMonitoringCGS?: boolean;
    isMonitoringReview?: boolean;
  }
): boolean {
  const { isBank, isMonitoring, isMonitoringCGS, isMonitoringReview } = roleFlags;
  
  // In edit mode, only allow the current tab
  if (isEditMode && tabName !== currentTab) {
    return true;
  }
  
  // Tab-specific disable logic
  switch (tabName) {
    case "bank":
      return !!(isMonitoring || isMonitoringCGS || isMonitoringReview);
    case "agency":
      return !!(isBank || isMonitoring || isMonitoringCGS || isMonitoringReview);
    case "monitoring":
      return !!(isBank || isMonitoringCGS || isMonitoringReview);
    case "monitoring_cgs":
      return !!(isBank || isMonitoring || isMonitoringReview);
    case "monitoring_review":
      return !!(isBank || isMonitoring || isMonitoringCGS);
    default:
      return false;
  }
}
